import React from "react";

export type ButtonVariant = "primary" | "secondary" | "muted";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

function baseClasses(disabled?: boolean) {
  const common = "inline-flex items-center justify-center rounded font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1";
  const disabledClasses = disabled ? " opacity-50 cursor-not-allowed" : " cursor-pointer";
  return common + disabledClasses;
}

function sizeClasses(size: ButtonSize) {
  switch (size) {
    case "sm":
      return " text-sm px-3 py-1.5";
    case "md":
    default:
      return " text-sm px-3 py-2";
  }
}

function variantClasses(variant: ButtonVariant) {
  if (variant === "secondary") {

    return " bg-white text-[#1D5176] border border-[#1D5176] hover:bg-[#F2F6F9]";
  }
  if (variant === "muted") {
    // Neutral disabled-like style (not dependent on disabled flag)
    return " bg-[#F3F4F6] text-[#9CA3AF] border border-[#D1D5DB]";
  }
  return " bg-[#1D5176] text-white hover:bg-[#17425E]";
}

export function getButtonClassName({
  variant = "primary",
  size = "md",
  disabled = false,
  className = "",
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  className?: string;
} = {}) {
  return (
    baseClasses(disabled) +
    sizeClasses(size) +
    " " +
    variantClasses(variant) +
    (variant === "muted" && disabled ? " opacity-100" : "") +
    (className ? " " + className : "")
  );
}

export const Button: React.FC<ButtonProps> = ({
  children,
  className = "",
  variant = "primary",
  size = "md",
  leftIcon,
  rightIcon,
  disabled,
  ...rest
}) => {
  return (
    <button
      className={getButtonClassName({ variant, size, disabled, className })}
      disabled={disabled}
      {...rest}
    >
      {leftIcon ? <span className="mr-2 -ml-0.5 flex items-center">{leftIcon}</span> : null}
      {children}
      {rightIcon ? <span className="ml-2 -mr-0.5 flex items-center">{rightIcon}</span> : null}
    </button>
  );
};

export default Button;

// Icon-only button (square) with color variants
type IconVariant = "primary" | "secondary" | "danger";
type IconSize = "sm" | "md";

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  variant?: IconVariant;
  size?: IconSize;
}

function iconVariantClasses(variant: IconVariant) {
  if (variant === "secondary") {
    return " bg-white text-[#1D5176] border border-[#1D5176] hover:bg-[#F2F6F9]";
  }
  if (variant === "danger") {
    return " bg-white text-[#B3261E] border border-[#B3261E] hover:bg-[#FFF5F5]";
  }
  return " bg-[#1D5176] text-white hover:bg-[#17425E]";
}

function iconSizeClasses(size: IconSize) {
  switch (size) {
    case "sm":
      return " w-9 h-9 text-sm";
    case "md":
    default:
      return " w-10 h-10 text-base";
  }
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  className = "",
  variant = "primary",
  size = "md",
  disabled,
  ...rest
}) => {
  return (
    <button
      className={
        "inline-flex items-center justify-center rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1" +
        (disabled ? " opacity-50 cursor-not-allowed" : " cursor-pointer") +
        iconSizeClasses(size) +
        " " +
        iconVariantClasses(variant) +
        (className ? " " + className : "")
      }
      disabled={disabled}
      {...rest}
    >
      {icon}
    </button>
  );
};


